/**
 * Company Links Cell
 * Coluna de vínculos da listagem: as quatro contagens que já chegam na lista e,
 * sob demanda, a lista dos itens que o resumo devolve.
 *
 * A contagem vem da própria listagem (subconsulta); a lista só é buscada quando
 * alguém abre o tooltip — no ponteiro, no foco ou no toque. Trazer as quatro
 * listas de 25 empresas seriam 100 listas por página para tooltips que quase
 * ninguém abre.
 *
 * Zero em âmbar só quando trava a operação: sem clínica não há onde agendar e
 * sem CNAE não sai grau de risco. Responsável e contato em zero ficam cinza.
 */

import React, { useState } from 'react';
import { Box, Button, CircularProgress, Tooltip, Typography } from '@mui/material';

import type { Company, CompanySummary } from '../../../types/company';
import { CONTACT_TYPE_LABELS, RESPONSIBLE_TYPE_LABELS } from '../../../types/company';

type LinkKey = 'providers' | 'responsibles' | 'contacts' | 'cnaes';

interface LinkConfig {
  key: LinkKey;
  label: string;
  title: string;
  /** O que a ausência impede — só aparece quando a contagem é zero. */
  empty: string;
  /** Zero aqui trava a operação da empresa. */
  blocking: boolean;
  count: (company: Company) => number;
}

/**
 * As contagens vêm da subconsulta da listagem e chegam como texto (`"0"`), ainda
 * que o tipo as declare `number`. Sem converter, `count === 0` nunca é verdade e
 * nenhum zero recebe cor.
 */
const contagem = (valor: number | undefined): number => {
  const numero = Number(valor ?? 0);
  return Number.isFinite(numero) ? numero : 0;
};

const LINKS: LinkConfig[] = [
  {
    key: 'providers',
    label: 'clín',
    title: 'Clínicas vinculadas',
    empty: 'Sem clínica — não há onde agendar',
    blocking: true,
    count: (company) => contagem(company.providerCount),
  },
  {
    key: 'responsibles',
    label: 'resp',
    title: 'Responsáveis técnicos',
    empty: 'Sem responsável técnico',
    blocking: false,
    count: (company) => contagem(company.responsibleCount),
  },
  {
    key: 'contacts',
    label: 'cont',
    title: 'Contatos',
    empty: 'Sem contato cadastrado',
    blocking: false,
    count: (company) => contagem(company.contactCount),
  },
  {
    key: 'cnaes',
    label: 'cnae',
    title: 'CNAEs',
    empty: 'Sem CNAE — o grau de risco não é calculado',
    blocking: true,
    count: (company) => contagem(company.cnaeCount),
  },
];

const MAX_ITEMS = 5;

const itemsOf = (summary: CompanySummary, key: LinkKey): string[] => {
  switch (key) {
    case 'providers':
      return summary.providers.items.map((item) =>
        item.serviceType ? `${item.name} · ${item.serviceType}` : item.name,
      );
    case 'responsibles':
      return summary.responsibles.items.map(
        (item) => `${item.name} · ${RESPONSIBLE_TYPE_LABELS[item.type] ?? item.type}`,
      );
    case 'contacts':
      return summary.contacts.items.map(
        (item) => `${item.name} · ${CONTACT_TYPE_LABELS[item.type] ?? item.type}`,
      );
    case 'cnaes':
      return summary.cnaes.items.map((item) => `${item.code} · ${item.description}`);
  }
};

interface LinkCounterProps {
  config: LinkConfig;
  company: Company;
  loadSummary: (companyId: string) => Promise<CompanySummary>;
}

const LinkCounter: React.FC<LinkCounterProps> = ({ config, company, loadSummary }) => {
  const [summary, setSummary] = useState<CompanySummary | null>(null);
  const [failed, setFailed] = useState(false);

  const count = config.count(company);
  const blocked = count === 0 && config.blocking;

  const handleOpen = (): void => {
    if (count === 0 || summary) return;

    loadSummary(company.id)
      .then((carregado) => {
        setSummary(carregado);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  };

  const items = summary ? itemsOf(summary, config.key).slice(0, MAX_ITEMS) : [];
  const remaining = count - items.length;

  const body = ((): React.ReactNode => {
    if (count === 0) return <Typography variant="caption">{config.empty}</Typography>;
    if (failed) return <Typography variant="caption">Não foi possível carregar a lista</Typography>;
    if (!summary) return <CircularProgress size={14} color="inherit" />;

    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {items.map((item) => (
          <Typography key={item} component="li" variant="caption" display="list-item">
            {item}
          </Typography>
        ))}
        {remaining > 0 && (
          <Typography component="li" variant="caption" display="list-item">
            e mais {remaining}
          </Typography>
        )}
      </Box>
    );
  })();

  return (
    <Tooltip
      onOpen={handleOpen}
      enterTouchDelay={0}
      leaveTouchDelay={6000}
      title={
        <Box sx={{ maxWidth: 260 }}>
          <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
            {config.title}
            {count > 0 ? ` · ${count}` : ''}
          </Typography>
          {body}
        </Box>
      }
    >
      <Button
        size="small"
        color={blocked ? 'warning' : 'inherit'}
        aria-label={`${config.title}: ${count}`}
        onClick={(event) => event.stopPropagation()}
        sx={{
          minWidth: 34,
          px: 0.5,
          py: 0.25,
          lineHeight: 1.1,
          textTransform: 'none',
          color: blocked ? 'warning.main' : count === 0 ? 'text.disabled' : undefined,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography component="span" variant="caption" fontWeight="bold" lineHeight={1.1}>
            {count}
          </Typography>
          <Typography component="span" sx={{ fontSize: 10, lineHeight: 1.1 }}>
            {config.label}
          </Typography>
        </Box>
      </Button>
    </Tooltip>
  );
};

export interface CompanyLinksCellProps {
  company: Company;
  /** Resolve o resumo da empresa com cache — ver `CompanyTable`. */
  loadSummary: (companyId: string) => Promise<CompanySummary>;
}

export const CompanyLinksCell: React.FC<CompanyLinksCellProps> = ({ company, loadSummary }) => (
  <Box sx={{ display: 'flex', gap: 0.25 }}>
    {LINKS.map((config) => (
      <LinkCounter key={config.key} config={config} company={company} loadSummary={loadSummary} />
    ))}
  </Box>
);

export default CompanyLinksCell;
